import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Header from "../../../../components/Header/Header";
import ResizablePanel from "../../../../components/ResizablePanel/ResizablePanel";
import InboxEmailList from "./components/InboxEmailList/InboxEmailList";
import InboxConversationPanel from "./components/InboxConversationPanel/InboxConversationPanel";
import { RootState } from "../../../../redux/store";
import {
  setThreads,
  appendThreads,
  clearThreads,
  updateThread,
  setMails,
  clearMails,
  setSelectedThread,
  clearSelectedThread,
  setSummary,
  setThreadsLoading,
  setMailsLoading,
  setLoadingMore,
  resetPagination,
  clearInboxState,
} from "../../../../redux/slices/crmInboxSlice";
import {
  useLazyGetInboxThreadsQuery,
  useLazyGetThreadMailsQuery,
  useGetInboxSummaryQuery,
  useAssignThreadAgentsMutation,
  useMarkThreadReadMutation,
} from "../../../../redux/services/crmApi";
import SearchBar from "../../../../components/SearchBar/SearchBar";
import { resetEmailEditor } from "../../../../redux/slices/emailEditorSlice";
import {
  openComposeEmailModal,
  setSelectedTab,
  saveDraftForLead,
} from "../../../../redux/slices/composeEmailModalSlice";
import FilterV2Icon from "../../../../assets/svg/filter-v2-icon";
import Button from "../../../../components/Button/Button";
import { selectCrmInboxActiveFilterCount } from "../../../../redux/slices/filterSlice";
import Filters from "../../../../components/Filters/Filters";
import { Avatar } from "../../../../components/Avatar/Avatar";
import type {
  InboxThread,
  ThreadSuggestionItem,
  SuggestionItem,
} from "./utils/types";
import { customToast } from "../../../../utils/custom-toast/custom-toast";

const InboxLayout = () => {
  const dispatch = useDispatch();

  const threads = useSelector((state: RootState) => state.crmInbox.threads);
  const mails = useSelector((state: RootState) => state.crmInbox.mails);
  const selectedThread = useSelector(
    (state: RootState) => state.crmInbox.selectedThread
  );
  const isThreadsLoading = useSelector(
    (state: RootState) => state.crmInbox.isThreadsLoading
  );
  const isMailsLoading = useSelector(
    (state: RootState) => state.crmInbox.isMailsLoading
  );
  const hasNext = useSelector(
    (state: RootState) => state.crmInbox.pagination.hasNext
  );
  const isLoadingMore = useSelector(
    (state: RootState) => state.crmInbox.pagination.isLoadingMore
  );
  const pagination = useSelector(
    (state: RootState) => state.crmInbox.pagination
  );
  const summary = useSelector((state: RootState) => state.crmInbox.summary);
  const filterCount = useSelector(selectCrmInboxActiveFilterCount);
  const inboxFilter = useSelector((state: RootState) => state.filter.crmInbox);

  const [selectedThreadId, setSelectedThreadId] = useState("");
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [emailListWidth, setEmailListWidth] = useState(380);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedInboxFilter, setSelectedInboxFilter] = useState("all");
  const [searchBarKey, setSearchBarKey] = useState(0);

  const threadIdRef = useRef<string | null>(null);
  const requestIdRef = useRef<string | null>(null);

  const [getInboxThreads] = useLazyGetInboxThreadsQuery();
  const [getThreadMails, { isFetching: isFetchingMails }] =
    useLazyGetThreadMailsQuery();

  const summaryParams = useMemo(
    () => ({
      ...(inboxFilter?.assignedAgents?.value?.length > 0 && {
        assigned_agent_ids: inboxFilter?.assignedAgents?.value,
      }),
      start_date: inboxFilter?.duration?.startDate,
      end_date: inboxFilter?.duration?.endDate,
    }),
    [inboxFilter]
  );

  const { data: summaryData } = useGetInboxSummaryQuery(summaryParams, {
    pollingInterval: selectedInboxFilter === "all" ? 5000 : 0,
  });

  const [assignThreadAgents] = useAssignThreadAgentsMutation();
  const [markThreadRead] = useMarkThreadReadMutation();

  useEffect(() => {
    if (summaryData?.summary) {
      dispatch(setSummary(summaryData.summary));
    }
  }, [summaryData, dispatch]);

  // ===== CORE HANDLERS =====

  const handleThreadSelect = useCallback(
    async (thread: InboxThread) => {
      if (threadIdRef.current === thread.thread_id) return;

      const currentRequestId = crypto.randomUUID();
      requestIdRef.current = currentRequestId;

      dispatch(clearMails());
      dispatch(setSelectedThread(thread));
      dispatch(setMailsLoading(true));

      setSelectedThreadId(thread.thread_id);
      threadIdRef.current = thread.thread_id;

      if (!thread.is_read) {
        markThreadRead({ thread_id: thread.thread_id });
        dispatch(updateThread({ ...thread, is_read: true }));
      }

      try {
        const response = await getThreadMails({
          thread_id: thread.thread_id,
        }).unwrap();

        if (requestIdRef.current !== currentRequestId) return;

        dispatch(setMails(response.mails));
        setShouldScrollToBottom(true);
      } catch (error) {
        if (requestIdRef.current === currentRequestId) {
          console.error("Failed to fetch thread mails:", error);
          dispatch(clearSelectedThread());
          dispatch(clearMails());
        }
      } finally {
        dispatch(setMailsLoading(false));
      }
    },
    [dispatch, getThreadMails, markThreadRead]
  );

  const fetchThreads = useCallback(
    async (isInitialLoad = true) => {
      try {
        if (isInitialLoad) {
          dispatch(resetPagination());
          dispatch(clearThreads());
        }
        dispatch(setThreadsLoading(true));

        const page = isInitialLoad ? 1 : pagination.page + 1;
        const mailType =
          selectedInboxFilter === "all" ? undefined : selectedInboxFilter;

        const response = await getInboxThreads({
          page,
          limit: 10,
          inbox_filter: mailType,
          ...(inboxFilter.assignedAgents.value.length > 0 && {
            assigned_agent_ids: inboxFilter.assignedAgents.value,
          }),
          start_date: inboxFilter.duration.startDate,
          end_date: inboxFilter.duration.endDate,
        }).unwrap();

        if (response?.threads) {
          if (isInitialLoad) {
            dispatch(
              setThreads({
                threads: response.threads,
                page: response.page,
                totalPages: response.total_pages,
                hasNext: response.has_next,
              })
            );

            if (response.threads.length > 0) {
              handleThreadSelect(response.threads[0]);
            } else {
              dispatch(clearMails());
              dispatch(clearSelectedThread());
              setSelectedThreadId("");
              threadIdRef.current = null;
            }
          } else {
            dispatch(
              appendThreads({
                threads: response.threads,
                page: response.page,
                totalPages: response.total_pages,
                hasNext: response.has_next,
              })
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch threads:", error);
        dispatch(clearMails());
        dispatch(clearSelectedThread());
        threadIdRef.current = null;
      } finally {
        dispatch(setThreadsLoading(false));
      }
    },
    [
      dispatch,
      getInboxThreads,
      selectedInboxFilter,
      pagination.page,
      handleThreadSelect,
      inboxFilter,
    ]
  );

  const loadMoreThreads = useCallback(async () => {
    if (!hasNext || isLoadingMore) return;
    dispatch(setLoadingMore(true));
    await fetchThreads(false);
  }, [hasNext, isLoadingMore, fetchThreads, dispatch]);

  const handleInboxFilterChange = useCallback(
    (value: string) => {
      setSelectedInboxFilter(value);
      threadIdRef.current = null;
      dispatch(clearMails());
      dispatch(clearSelectedThread());
      setSelectedThreadId("");
    },
    [dispatch]
  );

  const handleThreadApiSearch = useCallback(
    async (query: string): Promise<ThreadSuggestionItem[]> => {
      if (!query.trim()) return [];

      try {
        const result = await getInboxThreads({
          search: query,
          limit: 20,
          page: 1,
        }).unwrap();

        if (!result?.threads) return [];

        return result.threads.map(
          (thread): ThreadSuggestionItem => ({
            id: thread.thread_id,
            value: thread.from_email,
            content: (
              <div className="flex items-center p-3 gap-2">
                <Avatar text={thread.from_email || "?"} size={24} />
                <p className="text-data dark:text-data-dk font-medium text-md">
                  {thread.from_email}
                </p>
              </div>
            ),
            threadData: thread,
          })
        );
      } catch (error) {
        console.error("Thread search failed:", error);
        return [];
      }
    },
    [getInboxThreads]
  );

  const handleThreadSearchSelect = useCallback(
    (_value: string, item?: SuggestionItem) => {
      const threadItem = item as ThreadSuggestionItem;
      if (!threadItem?.threadData) return;

      dispatch(
        setThreads({
          threads: [threadItem.threadData],
          page: 1,
          totalPages: 1,
          hasNext: false,
        })
      );

      handleThreadSelect(threadItem.threadData);
    },
    [dispatch, handleThreadSelect]
  );

  const handleSearchClear = useCallback(() => {
    setSearchBarKey((prev) => prev + 1);
    threadIdRef.current = null;
    dispatch(clearMails());
    dispatch(clearSelectedThread());
    setSelectedThreadId("");
    fetchThreads(true);
  }, [dispatch, fetchThreads]);

  const handleAssignAgents = useCallback(
    async (agentIds: string[]) => {
      if (!selectedThread) return;
      try {
        const updatedThread = await assignThreadAgents({
          thread_id: selectedThread.thread_id,
          assigned_agents: agentIds,
        }).unwrap();
        dispatch(updateThread(updatedThread));
        customToast("Agents assigned successfully", "success");
      } catch (error) {
        customToast("Failed to assign agents", "error");
        console.error("Failed to assign agents:", error);
      }
    },
    [dispatch, selectedThread, assignThreadAgents]
  );

  const handleEmailBuilderClick = useCallback(
    (startTab?: string) => {
      if (!selectedThread?.from_email) return;

      dispatch(resetEmailEditor());
      dispatch(saveDraftForLead());
      dispatch(
        openComposeEmailModal({
          title: "New Email",
          viewState: startTab ? "expanded" : "mini",
          leadId: selectedThreadId,
          leadEmail: selectedThread?.from_email,
          source: "inbox",
          sourceMetadata: {
            extraAPIPayload: {
              thread_id: selectedThread.thread_id,
            },
            mailbox_email: selectedThread.mailbox_email,
            participants: selectedThread?.participants ?? [],
          },
          subject: `${selectedThread?.subject ? `Re: ${selectedThread?.subject}` : ""}`,
          isSubjectEditable: false,
          isReply: true,
        })
      );
      if (startTab) {
        dispatch(setSelectedTab(startTab));
      }
    },
    [dispatch, selectedThread, selectedThreadId]
  );

  const pollThreads = useCallback(async () => {
    try {
      const mailType =
        selectedInboxFilter === "all" ? undefined : selectedInboxFilter;
      const loadedCount = pagination?.page * 10;

      const response = await getInboxThreads({
        page: 1,
        limit: loadedCount,
        inbox_filter: mailType,
        ...(inboxFilter?.assignedAgents?.value?.length > 0 && {
          assigned_agent_ids: inboxFilter?.assignedAgents?.value,
        }),
        start_date: inboxFilter?.duration?.startDate,
        end_date: inboxFilter?.duration?.endDate,
      }).unwrap();

      if (response?.threads) {
        dispatch(
          setThreads({
            threads: response?.threads,
            page: pagination?.page,
            totalPages: response?.total_pages,
            hasNext: response?.has_next,
          })
        );
      }
    } catch {
      // silent fail for polling
    }
  }, [
    dispatch,
    getInboxThreads,
    selectedInboxFilter,
    inboxFilter,
    pagination?.page,
  ]);

  // ===== LIFECYCLE =====

  useEffect(() => {
    fetchThreads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInboxFilter, inboxFilter]);

  useEffect(() => {
    if (selectedInboxFilter !== "all") return;
    const interval = setInterval(pollThreads, 5000);
    return () => clearInterval(interval);
  }, [pollThreads, selectedInboxFilter]);

  useEffect(() => {
    return () => {
      dispatch(clearInboxState());
    };
  }, [dispatch]);

  const filterOptions = useMemo(
    () =>
      summary?.map((item) => ({
        label: item?.name,
        value: item?.id,
        count: item?.count,
      })),
    [summary]
  );

  return (
    <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk overflow-hidden">
      <Header>
        <div className="flex items-center">
          <div className="flex items-center gap-[8px]">
            <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              CRM
            </h1>
            <hr className="border border-border-gray dark:border-border-gray-dk h-[16px] w-1px" />
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              Inbox
            </p>
          </div>
          <div className="w-[55%] mx-auto flex gap-[8px] items-center justify-center">
            <SearchBar
              key={`inbox-search-${searchBarKey}`}
              id="inbox-search"
              placeholder="Search by subject, sender, agent name."
              noSuggestionsText="No threads found"
              enableApiSearch={true}
              onApiSearch={handleThreadApiSearch}
              onSelect={handleThreadSearchSelect}
              onClear={handleSearchClear}
              defaultValue={""}
              apiSearchDebounceMs={500}
              minCharsToSearch={1}
              suggestionsClassName="max-h-[300px]"
              containerClassName="w-[300px]"
              suggestionItemClassName="!p-0"
              className="hover:border-b-2 hover:border-b-[#1C6CFF] hover:shadow-[2px_4px_20px_rgba(0,0,0,0.1)] hover:rounded-[6px] !py-[5px]"
            />
          </div>
          <div className="flex items-center gap-[12px]">
            <Button
              innerText={
                <span className="flex items-center gap-1">
                  Filters
                  {filterCount > 0 && (
                    <span className="ml-1 rounded-full bg-fixed-white-light-and-dark-mode dark:bg-fixed-white-light-and-dark-mode-dk text-verba-blue dark:text-verba-blue-dk px-2 py-0.5 text-sm font-semibold">
                      {filterCount}
                    </span>
                  )}
                </span>
              }
              buttonContainerClass="py-2.5 px-3 bg-verba-blue dark:bg-verba-blue-dk rounded-full"
              innerTextClass="text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk text-md font-semibold"
              Icon={FilterV2Icon}
              onClick={() => setIsFiltersOpen(true)}
              iconClass="w-[18px] h-[18px] text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk"
              iconLeft={true}
            />
            <div className="w-px h-[12px] bg-border-gray dark:bg-border-gray-dk" />
            {isFiltersOpen && (
              <Filters
                openDrawer={isFiltersOpen}
                handleDrawerClose={() => setIsFiltersOpen(false)}
                size="380px"
                filterType="crm-inbox"
                modalities={[]}
              />
            )}
          </div>
        </div>
      </Header>

      <div className="flex-1 min-h-0 border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk flex overflow-hidden">
        {/* Left Panel - Thread List */}
        <ResizablePanel
          initialWidth={emailListWidth}
          minWidth={220}
          maxWidth={450}
          onWidthChange={setEmailListWidth}
          resizeDirection="right"
          className="h-full flex flex-col overflow-hidden"
        >
          <InboxEmailList
            threads={threads}
            selectedThreadId={selectedThreadId}
            isLoading={isThreadsLoading}
            hasMore={hasNext}
            isLoadingMore={isLoadingMore}
            selectedFilter={selectedInboxFilter}
            filterOptions={filterOptions}
            onFilterChange={handleInboxFilterChange}
            onThreadSelect={handleThreadSelect}
            onLoadMore={loadMoreThreads}
          />
        </ResizablePanel>

        {/* Middle Panel - Conversation */}
        <div className="flex-1 w-full h-full border-r border-l border-border-gray dark:border-border-gray-dk flex flex-col overflow-hidden">
          <InboxConversationPanel
            mails={mails}
            selectedThread={selectedThread}
            selectedThreadId={selectedThreadId}
            isLoading={isMailsLoading || isFetchingMails}
            shouldScrollToBottom={shouldScrollToBottom}
            setShouldScrollToBottom={setShouldScrollToBottom}
            onEmailBuilderClick={handleEmailBuilderClick}
            onAssignAgents={handleAssignAgents}
          />
        </div>
      </div>
    </div>
  );
};

export default InboxLayout;
